import React, { useMemo, useState } from 'react';
import { 
    Database, AlertCircle, Unlink, X, Type, Image, ArrowLeft, CheckCircle2, RefreshCw,
    ChevronRight, ChevronDown, Braces, Eye, EyeOff, GripVertical, Plus, Layers, Sparkles
} from 'lucide-react';
import { useGetCustomApisQuery } from '../../features/integrations/customApiApi';
import { toast } from 'react-hot-toast';
import { getFlattenedKeys, findFuzzyMatch } from '../../pages/template-editor/utils/autoMapper';
import { resolveJsonPath } from '../../pages/template-editor/utils/bindingResolver';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Layer } from '../../types/design';

interface BindableLayerInfo {
    id: string;
    name: string;
    type: string;
    binding?: {
        customApiId: string;
        apiKey: string;
        fallbackValue?: string;
    };
}

interface DataBindingSidepaneProps {
    layers: Layer[];
    textStyles: Record<string, any>;
    imageSettings: Record<string, any>;
    selectedLayerId: string | null;
    onSelectLayer: (id: string | null) => void;
    category?: string;
    onBindingChange: (layerId: string, binding: any | null) => void;
    onAutoMapAll?: (apiId: string, activeLayerBindingOverride?: any) => void;
    onAddBoundLayer?: (apiId: string, apiKey: string, value: string) => void;
    onClose: () => void;
    showPlaceholders: boolean;
    onShowPlaceholdersChange: (val: boolean) => void;
    autoPaginate?: boolean;
    onAutoPaginateChange?: (val: boolean) => void;
    templateSettings: any;
    onSettingsChange?: (updates: any) => void;
}

const TECHNICAL_KEYS = new Set([
    'headers', 'config', 'request', 'response', 'status', 'statuscode',
    'success', 'message', 'error', 'errors', 'ok', 'method', 'url', 'origin',
    'headers_list', 'header_values', 'request_body', 'api_url', 'token',
    'total', 'skip', 'limit', 'count', 'size', 'page', 'pages', 'totalpages', 'perpage',
    'data_json'
]);

// --- Recursive JSON Node Component ---
interface JSONNodeProps {
    name: string;
    value: any;
    path: string;
    depth: number;
    selectedApiKey: string;
    onSelectKey: (key: string) => void;
    onDragStart: (e: React.DragEvent, path: string, val: any) => void;
    onAdd?: (path: string, val: any) => void;
}

const JSONNode: React.FC<JSONNodeProps> = ({
    name,
    value,
    path,
    depth,
    selectedApiKey,
    onSelectKey,
    onDragStart,
    onAdd,
}) => {
    const [isOpen, setIsOpen] = useState(depth < 2);
    const isObject = value !== null && typeof value === 'object';
    const isArray = Array.isArray(value);
    const isSelected = selectedApiKey === path;

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleNodeDragStart = (e: React.DragEvent) => {
        onDragStart(e, path, value);
    };

    const renderValuePreview = (val: any) => {
        if (val === null) return <span className="text-gray-400 font-mono text-[10px] font-medium">null</span>;
        if (typeof val === 'boolean') return <span className="text-orange-500 font-mono text-[10px] font-medium">{String(val)}</span>;
        if (typeof val === 'number') return <span className="text-blue-500 font-mono text-[10px] font-medium">{val}</span>;
        
        const valStr = String(val);
        const isImg = valStr.startsWith('http') && /\.(jpg|jpeg|png|webp|gif|svg)/i.test(valStr.split('?')[0]);
        if (isImg) {
            return (
                <span className="text-teal-600 font-mono text-[10px] font-medium truncate flex items-center gap-1">
                    <span className="text-[9px] bg-teal-50 text-teal-600 border border-teal-200 px-1 py-0.2 rounded shrink-0">img</span>
                    "{valStr.length > 80 ? valStr.substring(0, 80) + '...' : valStr}"
                </span>
            );
        }
        return <span className="text-green-600 font-mono text-[10px] font-medium break-all">"{valStr.length > 80 ? valStr.substring(0, 80) + '...' : valStr}"</span>;
    };

    return (
        <div className="select-none font-sans text-xs">
            <div 
                className={`group flex items-center justify-between py-1 px-1.5 rounded transition-all ${
                    isSelected 
                        ? 'bg-[#30A84B]/10 border border-[#30A84B]/20 text-[#30A84B]' 
                        : 'hover:bg-gray-50 text-gray-700'
                }`}
                style={{ paddingLeft: `${depth * 12 + 6}px` }}
            >
                <div 
                    className="flex items-center gap-1.5 flex-1 min-w-0 cursor-pointer"
                    onClick={isObject ? handleToggle : () => onSelectKey(path)}
                    draggable="true"
                    onDragStart={handleNodeDragStart}
                >
                    {!isObject && (
                        <div className="opacity-0 group-hover:opacity-100 text-gray-450 cursor-grab shrink-0 transition-opacity">
                            <GripVertical className="w-3 h-3" />
                        </div>
                    )}
                    
                    {isObject ? (
                        <div className="text-gray-400 shrink-0">
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        </div>
                    ) : (
                        <div className="w-3.5 h-3.5 rounded bg-green-50 border border-green-200 flex items-center justify-center shrink-0">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#30A84B]" />
                        </div>
                    )}
                    
                    <span className={`font-mono text-[11px] truncate ${isObject ? 'text-purple-700 font-semibold' : 'text-gray-700 font-medium'}`}>
                        {name}
                    </span>
                    
                    {isObject ? (
                        <span className="text-gray-400 font-mono text-[10px] ml-1">
                            {isArray ? `[${value.length}]` : '{...}'}
                        </span>
                    ) : (
                        <span className="text-gray-400 font-mono text-[10px]">:</span>
                    )}
                    
                    {!isObject && (
                        <div className="flex-1 min-w-0 truncate text-[11px] ml-1">
                            {renderValuePreview(value)}
                        </div>
                    )}
                </div>

                {!isObject && onAdd && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAdd(path, value);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-green-100 text-[#30A84B] rounded cursor-pointer transition-opacity shrink-0 ml-1.5"
                        title="Bind or Add Layer"
                    >
                        <Plus className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            
            {isObject && isOpen && (
                <div className="relative border-l border-gray-100 ml-2.5 pl-0.5 mt-0.5">
                    {Object.entries(value)
                        .filter(([key, val]) => {
                            if (val === null || val === undefined) return false;
                            if (depth === 0 && TECHNICAL_KEYS.has(key.toLowerCase())) return false;
                            return true;
                        })
                        .map(([key, val]) => {
                            const isArrayParent = Array.isArray(value);
                            const nodeName = isArrayParent ? `[${key}]` : key;
                            const childPath = isArrayParent
                                ? `${path}[${key}]`
                                : (path ? `${path}.${key}` : key);

                            return (
                                <JSONNode
                                    key={childPath}
                                    name={nodeName}
                                    value={val}
                                    path={childPath}
                                    depth={depth + 1}
                                    selectedApiKey={selectedApiKey}
                                    onSelectKey={onSelectKey}
                                    onDragStart={onDragStart}
                                    onAdd={onAdd}
                                />
                            );
                        })}
                </div>
            )}
        </div>
    );
};

const getArrayLength = (apiData: any): number => {
    if (!apiData) return 1;
    if (Array.isArray(apiData)) return apiData.length;
    if (typeof apiData === 'object') {
        for (const key of Object.keys(apiData)) {
            if (Array.isArray(apiData[key])) {
                return apiData[key].length;
            }
        }
    }
    return 1;
};

export const DataBindingSidepane: React.FC<DataBindingSidepaneProps> = ({
    layers,
    textStyles,
    imageSettings,
    selectedLayerId,
    onSelectLayer,
    category = 'general',
    onBindingChange,
    onAutoMapAll,
    onAddBoundLayer,
    onClose,
    showPlaceholders,
    onShowPlaceholdersChange,
    autoPaginate = false,
    onAutoPaginateChange,
    templateSettings,
    onSettingsChange,
}) => {
    const { data, isLoading, refetch, isFetching } = useGetCustomApisQuery(undefined, {
        pollingInterval: 60000,
    });
    const customApis = data?.data || [];

    // Local storage-backed toggle state for auto-map
    const [autoMapEnabled, setAutoMapEnabled] = React.useState<boolean>(() => {
        return false;
    });

    const handleAutoMapToggle = (val: boolean) => {
        setAutoMapEnabled(val);
        localStorage.setItem('wejha_auto_map_enabled', String(val));
    };

    // Local storage-backed toggle state for the help guide
    const [isGuideOpen, setIsGuideOpen] = React.useState<boolean>(() => {
        const saved = localStorage.getItem('wejha_binding_guide_open');
        return saved !== null ? saved === 'true' : true;
    });

    const handleToggleGuide = () => {
        const newVal = !isGuideOpen;
        setIsGuideOpen(newVal);
        localStorage.setItem('wejha_binding_guide_open', String(newVal));
    };

    // View Mode State
    const [viewMode, setViewMode] = useState<'structure' | 'json'>('structure');

    // Filter APIs: Show matching category + general APIs
    const filteredApis = useMemo(() => {
        if (!category || category === 'general') return customApis;
        const catLower = category.toLowerCase();
        if (['weather', 'prayer', 'forex'].includes(catLower)) {
            return customApis.filter(api => api.api_type === catLower || api.api_type === 'general');
        }
        return customApis;
    }, [customApis, category]);

    // Flatten all layers recursively to find bindable ones
    const bindableLayers = useMemo((): BindableLayerInfo[] => {
        const result: BindableLayerInfo[] = [];
        const traverse = (list: Layer[]) => {
            for (const layer of list) {
                if (layer.type === 'text' || layer.type === 'image') {
                    result.push({
                        id: layer.id,
                        name: layer.name,
                        type: layer.type,
                        binding: layer.type === 'text'
                            ? textStyles[layer.id]?.binding
                            : imageSettings[layer.id]?.binding,
                    });
                }
                if (layer.children && layer.children.length > 0) {
                    traverse(layer.children);
                }
            }
        };
        traverse(layers);
        return result;
    }, [layers, textStyles, imageSettings]);

    // Find the currently active selected layer
    const activeBindableLayer = useMemo(() => {
        if (!selectedLayerId) return null;
        return bindableLayers.find(l => l.id === selectedLayerId) || null;
    }, [bindableLayers, selectedLayerId]);

    // Current binding states
    const activeBinding = activeBindableLayer?.binding;
    const selectedApiId = activeBinding?.customApiId || '';
    const selectedApiKey = activeBinding?.apiKey || '';
    const fallbackValue = activeBinding?.fallbackValue || '';

    const [globalApiId, setGlobalApiId] = React.useState<string>('');

    // Custom Dynamic Injections State
    const [isAddFormOpen, setIsAddFormOpen] = useState(false);
    const [customKey, setCustomKey] = useState('');
    const [customValue, setCustomValue] = useState('');


    const [injectionsTrigger, setInjectionsTrigger] = useState(0);



    React.useEffect(() => {
        if (!globalApiId) {
            const firstBound = bindableLayers.find(l => l.binding?.customApiId);
            if (firstBound && firstBound.binding?.customApiId) {
                setGlobalApiId(firstBound.binding.customApiId);
            }
        }
    }, [bindableLayers, globalApiId]);

    const activeApiId = activeBindableLayer ? selectedApiId : globalApiId;

    // Load custom variables from localStorage for this api
    const localInjections = useMemo(() => {
        if (!activeApiId) return {};
        try {
            const saved = localStorage.getItem(`wejha_api_injections_${activeApiId}`);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    }, [activeApiId, injectionsTrigger]);

    // Selected API object (merging database data with custom injected variables)
    const selectedApi = useMemo(() => {
        const api = customApis.find(a => String(a._id) === String(activeApiId));
        if (!api) return null;
        return {
            ...api,
            customapidata: {
                ...(api.customapidata || {}),
                ...localInjections
            }
        };
    }, [customApis, activeApiId, localInjections]);

    const arrayLength = useMemo(() => {
        return selectedApi ? getArrayLength(selectedApi.customapidata) : 1;
    }, [selectedApi]);



    // Live value resolution
    const liveValue = useMemo(() => {
        if (!selectedApi || !selectedApiKey) return null;
        const val = resolveJsonPath(selectedApi.customapidata, selectedApiKey);
        return val !== undefined && val !== null ? String(val) : null;
    }, [selectedApi, selectedApiKey]);



    // Flattened keys of the selected API




    const handleApiChange = (apiId: string) => {
        if (!activeBindableLayer) {
            setGlobalApiId(apiId === 'none' ? '' : apiId);
            // Commented out automatic auto-mapping on integration source selection change:
            // if (apiId && apiId !== 'none' && onAutoMapAll) {
            //     onAutoMapAll(apiId);
            // }
            return;
        }
        if (!apiId || apiId === 'none') {
            onBindingChange(activeBindableLayer.id, null);
            return;
        }

        const nextApi = customApis.find(api => String(api._id) === String(apiId));
        const keys = nextApi && nextApi.customapidata ? getFlattenedKeys(nextApi.customapidata) : [];

        let finalKey = '';

        if (autoMapEnabled) {
            if (activeBindableLayer.name && keys.length > 0) {
                const match = findFuzzyMatch(activeBindableLayer.name, keys, category);
                if (match) {
                    finalKey = match;
                }
            }

            if (onAutoMapAll) {
                const newBinding = {
                    customApiId: apiId,
                    apiKey: finalKey,
                    fallbackValue: fallbackValue,
                };
                onAutoMapAll(apiId, newBinding);
                return;
            }
        }

        onBindingChange(activeBindableLayer.id, {
            customApiId: apiId,
            apiKey: finalKey,
            fallbackValue: fallbackValue,
        });
    };

    const handleKeyChange = (key: string) => {
        const targetLayerId = activeBindableLayer?.id;
        const targetApiId = activeBindableLayer ? selectedApiId : globalApiId;
        if (!targetApiId) return;

        if (targetLayerId) {
            onBindingChange(targetLayerId, {
                customApiId: targetApiId,
                apiKey: key,
                fallbackValue,
            });
        }
    };

    const handleFallbackChange = (val: string) => {
        if (!activeBindableLayer || !selectedApiId || !selectedApiKey) return;
        onBindingChange(activeBindableLayer.id, {
            customApiId: selectedApiId,
            apiKey: selectedApiKey,
            fallbackValue: val,
        });
    };

    const handleAddCustomVariable = (key: string, value: string) => {
        const targetId = activeBindableLayer ? selectedApiId : globalApiId;
        if (!targetId) return;
        
        const updated = {
            ...localInjections,
            [key]: value
        };
        localStorage.setItem(`wejha_api_injections_${targetId}`, JSON.stringify(updated));
        setInjectionsTrigger(prev => prev + 1);
        
        if (activeBindableLayer) {
            onBindingChange(activeBindableLayer.id, {
                customApiId: targetId,
                apiKey: key,
                fallbackValue: value,
            });
        }
        toast.success(`Injected custom variable: "${key}"`);
    };

    const handleUnbind = () => {
        if (!activeBindableLayer) return;
        onBindingChange(activeBindableLayer.id, null);
    };

    // HTML5 Drag Start Handler
    const handleDragStart = (e: React.DragEvent, apiKey: string, value: any) => {
        const targetApiId = activeBindableLayer ? selectedApiId : globalApiId;
        const dragData = {
            type: 'data-binding',
            data: {
                customApiId: targetApiId,
                apiKey,
                value: String(value ?? '')
            }
        };
        e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = 'copy';
        
        try {
            const dragIcon = document.createElement('div');
            dragIcon.className = 'px-3 py-1.5 bg-[#30A84B] text-white text-xs font-bold rounded-lg shadow-lg flex items-center gap-1.5 absolute -top-100 -left-100';
            dragIcon.innerHTML = `🔌 Bind: ${apiKey.split('.').pop()}`;
            document.body.appendChild(dragIcon);
            e.dataTransfer.setDragImage(dragIcon, 10, 10);
            setTimeout(() => document.body.removeChild(dragIcon), 0);
        } catch (err) { /* ignore */ }
    };

    const handleQuickAction = (key: string, val: any) => {
        const targetApiId = activeBindableLayer ? selectedApiId : globalApiId;
        if (!targetApiId) return;
        
        if (activeBindableLayer) {
            handleKeyChange(key);
        } else if (onAddBoundLayer) {
            onAddBoundLayer(targetApiId, key, String(val ?? ''));
        }
    };

    return (
        <div className="w-[360px] h-full bg-white shadow-2xl flex flex-col border-l border-[#e4e7ec] animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="h-[68px] flex items-center justify-between px-5 border-b border-[#e4e7ec] bg-white shrink-0">
                <div className="flex items-center gap-3 overflow-hidden">
                    <div className="w-8 h-8 rounded-lg bg-[#30A84B]/10 flex items-center justify-center shrink-0">
                        <Database className="w-4 h-4 text-[#30A84B]" />
                    </div>
                    <div>
                        <h3 className="font-sans font-semibold text-[#1d2739] text-[16px] truncate">Dynamic Data Feed</h3>
                        <p className="text-[10px] text-[#98a2b3] font-bold uppercase tracking-widest leading-none mt-0.5 font-sans">
                            {activeBindableLayer ? 'Layer Data Pairing' : 'Canvas Bindings Dashboard'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => refetch()}
                        className={`text-[#667185] hover:text-[#1d2739] transition-all p-1.5 rounded-md hover:bg-gray-100 cursor-pointer ${
                            isFetching ? 'animate-spin text-[#30A84B]' : ''
                        }`}
                        title="Sync with latest DB values"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[#667185] hover:text-[#1d2739] transition-colors p-1.5 rounded-md hover:bg-gray-100 cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto p-5 scrollbar-none bg-white">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-xs text-gray-500 font-sans">
                        <div className="w-6 h-6 border-2 border-t-transparent border-[#30A84B] rounded-full animate-spin"></div>
                        <span>Loading API integrations...</span>
                    </div>
                ) : customApis.length === 0 ? (
                    <div className="py-20 text-center font-sans space-y-4">
                        <Database className="w-12 h-12 text-gray-300 mx-auto" />
                        <h4 className="font-bold text-gray-700 text-sm">No API Integrations Connected</h4>
                        <p className="text-xs text-gray-500 leading-relaxed max-w-[200px] mx-auto">
                            Setup external data feeds (Weather, Prayer, Forex feeds) in the integrations page to bind variables to layers.
                        </p>
                        <a
                            href="/integrations"
                            target="_blank"
                            className="inline-block px-4 py-2 border border-[#30A84B] text-[#30A84B] rounded-lg font-bold text-xs hover:bg-[#30A84B]/5 transition-colors"
                        >
                            Configure integrations →
                        </a>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* 1. Context Information Mode */}
                        {activeBindableLayer ? (
                            <div className="space-y-4 animate-in fade-in zoom-in-95 duration-200">
                                <button
                                    type="button"
                                    onClick={() => onSelectLayer(null)}
                                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors font-semibold"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5" /> Back to bindings list
                                </button>

                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5 overflow-hidden">
                                        <div className="p-2 bg-white rounded-lg border border-gray-150 text-gray-600 shrink-0">
                                            {activeBindableLayer.type === 'text' ? (
                                                <Type className="w-4 h-4 text-[#30A84B]" />
                                            ) : (
                                                <Image className="w-4 h-4 text-blue-500" />
                                            )}
                                        </div>
                                        <div className="overflow-hidden">
                                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider leading-none">Selected Layer</span>
                                            <span className="block font-sans font-semibold text-gray-800 text-sm truncate mt-1">{activeBindableLayer.name}</span>
                                        </div>
                                    </div>
                                    {activeBinding && (
                                        <button
                                            type="button"
                                            onClick={handleUnbind}
                                            className="text-[11px] font-bold text-red-500 hover:text-red-700 flex items-center gap-1 px-2.5 py-1 bg-red-50 hover:bg-red-100/50 rounded-lg transition-colors cursor-pointer"
                                        >
                                            <Unlink className="w-3 h-3" /> Unbind
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gradient-to-br from-green-50/40 to-green-50/10 border border-[#30A84B]/15 rounded-xl p-3.5 space-y-2.5 shadow-sm">
                                <div 
                                    className="flex items-center justify-between cursor-pointer select-none"
                                    onClick={handleToggleGuide}
                                    title={isGuideOpen ? "Collapse guide" : "Expand guide"}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="w-5.5 h-5.5 rounded-md bg-[#30A84B]/10 flex items-center justify-center shrink-0">
                                            <Sparkles className="w-3.5 h-3.5 text-[#30A84B]" />
                                        </div>
                                        <h4 className="font-bold text-gray-805 text-xs font-sans">How to Connect Feed</h4>
                                    </div>
                                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider select-none shrink-0">
                                        {isGuideOpen ? 'Hide' : 'Show'}
                                    </span>
                                </div>
                                {isGuideOpen && (
                                    <div className="space-y-2 text-xs text-gray-650 leading-relaxed font-sans border-t border-green-100/50 pt-2.5 animate-in slide-in-from-top-2 duration-200">
                                        <p className="font-semibold text-gray-700">Get your template running with live data in 3 easy steps:</p>
                                        <ul className="space-y-2 pl-1">
                                            <li className="flex gap-2">
                                                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#30A84B]/10 text-[#30A84B] font-bold text-[9px] shrink-0 mt-0.5">1</span>
                                                <span><strong>Select Integration Source</strong> below to connect your data feed.</span>
                                            </li>
                                            <li className="flex gap-2">
                                                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#30A84B]/10 text-[#30A84B] font-bold text-[9px] shrink-0 mt-0.5">2</span>
                                                <span><strong>Auto-Map:</strong> The editor will instantly bind all matching fields to your designed layers!</span>
                                            </li>
                                            <li className="flex gap-2">
                                                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#30A84B]/10 text-[#30A84B] font-bold text-[9px] shrink-0 mt-0.5">3</span>
                                                <span><strong>Custom Bind:</strong> Click any layer on the canvas, then select/click any variable from the list below to pair it manually.</span>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 2. Global Bindings Info - Show when no layer is selected */}
                        {!activeBindableLayer && bindableLayers.length > 0 && (
                            <div className="space-y-2.5">
                                <div className="flex items-center gap-2">
                                    <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Active Canvas Layers</h4>
                                    <div className="h-px w-full bg-[#f0f2f5]" />
                                </div>
                                <div className="space-y-2 max-h-[160px] overflow-y-auto custom-scrollbar p-0.5">
                                    {bindableLayers.map((layer) => {
                                        const isBound = !!layer.binding;
                                        const boundApi = isBound ? customApis.find(api => String(api._id) === String(layer.binding?.customApiId)) : null;

                                        return (
                                            <div
                                                key={layer.id}
                                                onClick={() => onSelectLayer(layer.id)}
                                                className="p-2.5 bg-white hover:bg-gray-50/60 rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 cursor-pointer flex items-center justify-between group"
                                            >
                                                <div className="flex items-center gap-3 overflow-hidden">
                                                    <div className="p-2 bg-gray-50 rounded-lg border border-gray-100 group-hover:bg-white transition-colors shrink-0 text-gray-500">
                                                        {layer.type === 'text' ? (
                                                            <Type className="w-3.5 h-3.5 text-[#30A84B]/80" />
                                                        ) : (
                                                            <Image className="w-3.5 h-3.5 text-blue-500/80" />
                                                        )}
                                                    </div>
                                                    <div className="overflow-hidden">
                                                        <span className="block font-sans font-semibold text-gray-800 text-xs truncate">{layer.name}</span>
                                                        <span className="block text-[9px] text-gray-400 font-medium leading-none mt-1">
                                                            {isBound && boundApi ? (
                                                                <span className="text-[#30A84B] font-semibold flex items-center gap-1">
                                                                    <CheckCircle2 className="w-2.5 h-2.5 text-[#30A84B]" />
                                                                    {boundApi.name_custom} ➜ {layer.binding?.apiKey.split('.').pop()}
                                                                </span>
                                                            ) : (
                                                                'Static Content (No binding)'
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="w-5 h-5 rounded-full bg-gray-50 group-hover:bg-[#30A84B]/10 flex items-center justify-center shrink-0 text-gray-455 group-hover:text-[#30A84B] transition-all text-xs">
                                                    ➜
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* 3. API Source Selector */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Data Source</h4>
                                <div className="h-px w-full bg-[#f0f2f5]" />
                            </div>

                            <div className="space-y-1.5">
                                <label className="block text-[11px] font-semibold text-gray-650">
                                    Integration Source
                                </label>
                                <Select
                                    value={activeBindableLayer ? (selectedApiId ? String(selectedApiId) : 'none') : (globalApiId ? String(globalApiId) : 'none')}
                                    onValueChange={handleApiChange}
                                >
                                    <SelectTrigger className="w-full h-10 px-3 border border-gray-250 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30A84B]/10 focus:border-[#30A84B] bg-white text-sm text-gray-700 font-sans shadow-none">
                                        <SelectValue placeholder="Select integration..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white border border-gray-200 shadow-2xl rounded-lg z-[99999] max-h-56">
                                        <SelectItem value="none" className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">Select an integration...</SelectItem>
                                        {filteredApis.map((api) => {
                                            const isMatch = api.api_type === category.toLowerCase();
                                            const apiIdStr = api._id ? String(api._id) : '';
                                            return (
                                                <SelectItem key={apiIdStr} value={apiIdStr} className="text-sm py-2 cursor-pointer focus:bg-[#30A84B]/5 focus:text-[#30A84B]">
                                                    {api.name_custom} {isMatch ? '⭐' : ''}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>





                            {/* Add Custom Variable Form */}
                            {((activeBindableLayer && selectedApiId) || (!activeBindableLayer && globalApiId)) && (
                                <div className="p-3 bg-gray-50/50 rounded-xl border border-gray-200/60 space-y-2.5">
                                    <div className="flex items-center justify-between cursor-pointer select-none" onClick={() => setIsAddFormOpen(!isAddFormOpen)}>
                                        <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                                            <Plus className="w-3.5 h-3.5 text-[#30A84B]" />
                                            Add Custom Variable
                                        </span>
                                        <span className="text-[10px] text-gray-400 font-semibold">{isAddFormOpen ? 'Collapse' : 'Expand'}</span>
                                    </div>
                                    {isAddFormOpen && (
                                        <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-gray-450 uppercase tracking-wider block">Variable Key Name</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. PromoMessage"
                                                    value={customKey}
                                                    onChange={(e) => setCustomKey(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                                                    className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-sans outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B] bg-white text-gray-700"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[9px] font-bold text-gray-450 uppercase tracking-wider block">Sample Value</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g. Special 50% discount!"
                                                    value={customValue}
                                                    onChange={(e) => setCustomValue(e.target.value)}
                                                    className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-sans outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B] bg-white text-gray-700"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (!customKey.trim() || !customValue.trim()) return;
                                                    handleAddCustomVariable(customKey.trim(), customValue.trim());
                                                    setCustomKey('');
                                                    setCustomValue('');
                                                }}
                                                disabled={!customKey.trim() || !customValue.trim()}
                                                className="w-full py-1.5 bg-[#30A84B] hover:bg-[#25873a] disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1 shadow-sm"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                                Add Variable
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Dynamic Toggles and Auto-mapping */}
                            {((activeBindableLayer && selectedApiId) || (!activeBindableLayer && globalApiId)) && (
                                <div className="space-y-3">
                                    {/* Placeholder Mode Toggle */}
                                    <div className="flex items-center justify-between p-3.5 bg-green-50/20 rounded-xl border border-green-100 select-none">
                                        <div className="space-y-0.5">
                                            <span className="block text-xs font-bold text-gray-800 flex items-center gap-1.5">
                                                {showPlaceholders ? <Eye className="w-3.5 h-3.5 text-[#30A84B]" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                                                Show Placeholders
                                            </span>
                                            <span className="block text-[10px] text-gray-400">Display template tags on canvas</span>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer select-none">
                                            <input
                                                type="checkbox"
                                                checked={showPlaceholders}
                                                onChange={(e) => onShowPlaceholdersChange(e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#30A84B]"></div>
                                        </label>
                                    </div>

                                    {/* Auto-Generate Pages in Preview Toggle with Settings */}
                                    <div className="flex flex-col p-3.5 bg-blue-50/10 rounded-xl border border-blue-100 select-none space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-0.5">
                                                <span className="block text-xs font-bold text-gray-800 flex items-center gap-1.5">
                                                    <Layers className="w-3.5 h-3.5 text-blue-500" />
                                                    Auto-Generate Pages in Preview
                                                </span>
                                                <span className="block text-[10px] text-gray-400">Generate dynamic pages on preview</span>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={autoPaginate}
                                                    onChange={(e) => onAutoPaginateChange?.(e.target.checked)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
                                            </label>
                                        </div>

                                        <div className="border-t border-blue-100/50 pt-3 space-y-3">
                                            {autoPaginate ? (
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-bold text-gray-500 block">Start Page</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={templateSettings?.startIndex || 1}
                                                            onChange={(e) => onSettingsChange?.({ startIndex: Math.max(1, parseInt(e.target.value) || 1) })}
                                                            className="w-full px-2.5 py-1 border border-gray-250 rounded-lg text-xs bg-white text-gray-705 outline-none focus:border-blue-500"
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-[10px] font-bold text-gray-500 block">End Page Limit</label>
                                                        <Select
                                                            value={String(templateSettings?.endIndex || 5)}
                                                            onValueChange={(val) => {
                                                                const numVal = val === 'array.length' ? 'array.length' : parseInt(val);
                                                                onSettingsChange?.({ endIndex: numVal });
                                                            }}
                                                        >
                                                            <SelectTrigger className="w-full h-8 px-2.5 border border-gray-250 rounded-lg text-xs bg-white text-gray-705 outline-none focus:border-blue-500 select-none shadow-none">
                                                                <SelectValue placeholder="Select limit..." />
                                                            </SelectTrigger>
                                                            <SelectContent className="bg-white border border-gray-250 shadow-xl rounded-lg z-[99999]">
                                                                {arrayLength <= 1 ? (
                                                                    <SelectItem value="1" className="text-xs">1 Page</SelectItem>
                                                                ) : (
                                                                    <>
                                                                        {arrayLength >= 2 && <SelectItem value="2" className="text-xs">2 Pages</SelectItem>}
                                                                        {arrayLength >= 5 && <SelectItem value="5" className="text-xs">5 Pages</SelectItem>}
                                                                        {arrayLength >= 10 && <SelectItem value="10" className="text-xs">10 Pages</SelectItem>}
                                                                        {arrayLength >= 20 && <SelectItem value="20" className="text-xs">20 Pages</SelectItem>}
                                                                        {![2, 5, 10, 20].includes(arrayLength) && (
                                                                            <SelectItem value={String(arrayLength)} className="text-xs">{arrayLength} Pages</SelectItem>
                                                                        )}
                                                                    </>
                                                                )}
                                                                <SelectItem value="array.length" className="text-xs font-mono font-bold text-blue-600">n = array.length ({arrayLength})</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="space-y-1">
                                                    <label className="text-[10px] font-bold text-gray-500 block">Single Item Selection</label>
                                                    <Select
                                                        value={String(templateSettings?.singleItemIndex || 1)}
                                                        onValueChange={(val) => {
                                                            const numVal = val === 'array.length' ? 'array.length' : parseInt(val);
                                                            onSettingsChange?.({ singleItemIndex: numVal });
                                                        }}
                                                    >
                                                        <SelectTrigger className="w-full h-8 px-2.5 border border-gray-250 rounded-lg text-xs bg-white text-gray-705 outline-none focus:border-blue-500 select-none shadow-none">
                                                            <SelectValue placeholder="Select index..." />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-white border border-gray-250 shadow-xl rounded-lg z-[99999] max-h-56">
                                                            {Array.from({ length: arrayLength }).map((_, idx) => {
                                                                const itemNumber = idx + 1;
                                                                const getOrdinalSuffix = (i: number) => {
                                                                    const j = i % 10, k = i % 100;
                                                                    if (j === 1 && k !== 11) return `${i}st`;
                                                                    if (j === 2 && k !== 12) return `${i}nd`;
                                                                    if (j === 3 && k !== 13) return `${i}rd`;
                                                                    return `${i}th`;
                                                                };
                                                                return (
                                                                    <SelectItem key={itemNumber} value={String(itemNumber)} className="text-xs">
                                                                        {getOrdinalSuffix(itemNumber)} Item [{idx}]
                                                                    </SelectItem>
                                                                );
                                                            })}
                                                            <SelectItem value="array.length" className="text-xs font-mono font-bold text-blue-600">n = array.length ({arrayLength})</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}

                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-gray-500 block">Interval Between Pages (sec)</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={templateSettings?.pageInterval || 5}
                                                    onChange={(e) => onSettingsChange?.({ pageInterval: Math.max(1, parseInt(e.target.value) || 1) })}
                                                    className="w-full px-2.5 py-1 border border-gray-250 rounded-lg text-xs bg-white text-gray-705 outline-none focus:border-blue-500"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Auto-mapping Toggle Switch - Active Layer Only (Hidden as requested) */}
                                    {false && activeBindableLayer && selectedApiId && (
                                        <div className="flex items-center justify-between p-3.5 bg-gray-50/70 rounded-xl border border-gray-200/50 select-none">
                                            <div className="space-y-0.5">
                                                <span className="block text-xs font-bold text-gray-700">Auto-map matching layers</span>
                                                <span className="block text-[10px] text-gray-400">Match layer names dynamically</span>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={autoMapEnabled}
                                                    onChange={(e) => handleAutoMapToggle(e.target.checked)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#30A84B]"></div>
                                            </label>
                                        </div>
                                    )}

                                    {/* Auto-map all layers button (Hidden as requested) */}
                                    {false && activeBindableLayer && selectedApiId && onAutoMapAll && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const bindingOverride = {
                                                    customApiId: selectedApiId,
                                                    apiKey: selectedApiKey,
                                                    fallbackValue: fallbackValue
                                                };
                                                onAutoMapAll?.(selectedApiId, bindingOverride);
                                            }}
                                            className="w-full py-2.5 px-4 border border-[#30A84B] text-[#30A84B] rounded-lg hover:bg-[#30A84B]/5 text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
                                            title="Scan and automatically bind all matching layers in the template to this API"
                                        >
                                            <span>🔌 Auto-map all layers</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 4. Display Views and View Switcher */}
                        {((activeBindableLayer && selectedApiId) || (!activeBindableLayer && globalApiId)) && (
                            <div className="space-y-4 animate-in fade-in duration-200">
                                {/* Section Divider */}
                                <div className="flex items-center gap-2">
                                    <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Variable Mapping</h4>
                                    <div className="h-px w-full bg-[#f0f2f5]" />
                                </div>

                                {/* View Switcher Tabs */}
                                <div className="grid grid-cols-2 gap-1 p-1 bg-gray-50 rounded-xl border border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('structure')}
                                        className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                            viewMode === 'structure' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'
                                        }`}
                                    >
                                        <Layers className="w-3.5 h-3.5 text-blue-500" />
                                        <span>Structure</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('json')}
                                        className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                            viewMode === 'json' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'
                                        }`}
                                    >
                                        <Braces className="w-3.5 h-3.5 text-blue-500" />
                                        <span>Current JSON</span>
                                    </button>
                                </div>

                                {/* Content Containers */}
                                <div className="border border-gray-200 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto bg-gray-50/30 p-2 custom-scrollbar">
                                    {selectedApi && selectedApi.customapidata ? (
                                        <>
                                            {/* Structure Tab (Interactive JSON Tree) */}
                                            {viewMode === 'structure' && (
                                                <div className="space-y-1.5 py-1 px-1">
                                                    <JSONNode
                                                        name="root"
                                                        value={selectedApi.customapidata}
                                                        path=""
                                                        depth={0}
                                                        selectedApiKey={selectedApiKey}
                                                        onSelectKey={handleKeyChange}
                                                        onDragStart={handleDragStart}
                                                        onAdd={handleQuickAction}
                                                    />
                                                </div>
                                            )}

                                            {/* Current JSON Tab (Raw JSON viewer) */}
                                            {viewMode === 'json' && (
                                                <pre className="font-mono text-[10px] bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-[380px] whitespace-pre select-text">
                                                    {JSON.stringify(selectedApi.customapidata, null, 2)}
                                                </pre>
                                            )}
                                        </>
                                    ) : (
                                        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-800 leading-normal">
                                            This integration has no cached data. Test it first in the Integrations dashboard to enable path variables.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 5. Live Value Card */}
                        {selectedApiId && selectedApiKey && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <h4 className="text-[10px] font-black text-[#98a2b3] uppercase tracking-[0.2em] whitespace-nowrap font-sans">Response Preview</h4>
                                    <div className="h-px w-full bg-[#f0f2f5]" />
                                </div>

                                <div className="bg-[#30A84B]/5 border border-[#30A84B]/20 rounded-xl p-4 space-y-3.5">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-black text-[#30A84B] uppercase tracking-wider">Resolved value</span>
                                        <span className="text-[9px] bg-[#30A84B]/10 text-[#30A84B] px-2.5 py-0.5 rounded-full font-bold">Active</span>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-[13px] font-semibold text-gray-800 break-all leading-relaxed bg-white p-3 rounded-lg border border-gray-200/70 shadow-sm max-h-32 overflow-y-auto">
                                            {liveValue !== null ? (
                                                (liveValue.startsWith('http') && /\.(jpg|jpeg|png|webp|gif|svg)/i.test(liveValue.split('?')[0])) ? (
                                                    <div className="space-y-2">
                                                        <img src={liveValue} alt="Live preview" className="h-16 w-auto rounded border border-gray-200 object-contain mx-auto" />
                                                        <span className="text-[10px] text-gray-400 block truncate text-center">{liveValue}</span>
                                                    </div>
                                                ) : (
                                                    liveValue
                                                )
                                            ) : (
                                                <span className="text-amber-600 italic flex items-center gap-1.5 font-semibold">
                                                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" /> Using fallback (no data resolved)
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-gray-400 leading-normal flex gap-1">
                                            <span>Path:</span>
                                            <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-650 font-mono text-[10px] truncate max-w-[200px]" title={selectedApiKey}>{selectedApiKey}</code>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Fallback Value Input */}
                        {activeBindableLayer && selectedApiId && selectedApiKey && (
                            <div className="space-y-1.5 pt-1">
                                <label className="block text-[11px] font-semibold text-gray-600">
                                    Fallback Value
                                </label>
                                <input
                                    type="text"
                                    placeholder="Fallback if API is unavailable"
                                    value={fallbackValue}
                                    onChange={(e) => handleFallbackChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-250 rounded-lg focus:outline-none focus:border-[#30A84B] focus:ring-2 focus:ring-[#30A84B]/10 text-xs text-gray-700 font-sans transition-all"
                                />
                            </div>
                        )}

                        {/* Warnings if not bound */}
                        {activeBindableLayer && selectedApiId && !selectedApiKey && (
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2.5 animate-in fade-in duration-200">
                                <div className="flex items-center gap-1.5 text-amber-800 font-bold text-[10px] uppercase tracking-wider">
                                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                    <span>Not Mapped</span>
                                </div>
                                <p className="text-xs text-amber-700 leading-relaxed">
                                    {autoMapEnabled
                                        ? "No matching variables were found in this source for this layer name. Turn OFF Auto-map to bind a path manually."
                                        : "Please select a variable path or node to bind this layer's content."}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
